import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '@/api/client'
import type { AtsType, CompanyCategory, Job, JobFilters, Market } from '@/api/types'
import { buttonClasses } from '@/components/ui/Button'
import {
  Badge,
  EmptyState,
  ErrorBox,
  PageHeader,
  Pagination,
  Segmented,
  Select,
  SkeletonRows,
} from '@/components/ui/Primitives'
import { JobDrawer } from '@/features/jobs/JobDrawer'
import {
  ATS_LABEL,
  ATS_TYPES,
  CATEGORIES,
  CATEGORY_LABEL,
  EXPERIENCE_BUCKETS,
  MARKET_FLAG,
  experienceLabel,
  formatSalary,
  humanise,
  relativeTime,
} from '@/lib/utils'
import { useUrlState } from '@/lib/useUrlState'

const PAGE_SIZE = 25

const COUNTRIES: Array<[string, string]> = [
  ['IN', 'India'],
  ['US', 'United States'],
  ['GB', 'United Kingdom'],
  ['CA', 'Canada'],
  ['SG', 'Singapore'],
  ['DE', 'Germany'],
  ['IE', 'Ireland'],
  ['AE', 'UAE'],
  ['AU', 'Australia'],
]

export function JobsPage() {
  const [filters, setFilters] = useUrlState({
    q: '',
    market: '',
    country_code: '',
    ats_type: '',
    source_id: '',
    company_id: '',
    company: '',
    category: '',
    tier: '',
    remote: '',
    it: '',
    posted_within_days: '',
    found_within_days: '',
    new_only: '',
    job_function: '',
    seniority: '',
    skills: '',
    exp: '',
    active: 'true',
    sort: 'posted',
    page: '1',
    job: '',
  })
  const [search, setSearch] = useState(filters.q)
  const [company, setCompany] = useState(filters.company)
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (search !== filters.q || company !== filters.company) {
        setFilters({ q: search, company, sort: search && !filters.q ? 'relevance' : filters.sort })
      }
    }, 300)
    return () => window.clearTimeout(t)
  }, [search, company, filters.q, filters.company, filters.sort, setFilters])

  const query: JobFilters = {
    q: filters.q,
    market: filters.market as Market | '',
    country_code: filters.country_code,
    ats_type: filters.ats_type as AtsType | '',
    source_id: filters.source_id ? Number(filters.source_id) : '',
    company_id: filters.company_id ? Number(filters.company_id) : '',
    company: filters.company,
    category: filters.category as CompanyCategory | '',
    tier: filters.tier ? Number(filters.tier) : '',
    found_within_days: filters.found_within_days ? Number(filters.found_within_days) : '',
    new_only: filters.new_only === 'true',
    job_function: filters.job_function,
    seniority: filters.seniority,
    skills: filters.skills,
    experience_from: filters.exp ? Number(EXPERIENCE_BUCKETS.find((b) => b[0] === filters.exp)?.[2].split(',')[0]) : '',
    experience_to: filters.exp ? Number(EXPERIENCE_BUCKETS.find((b) => b[0] === filters.exp)?.[2].split(',')[1]) : '',
    remote: filters.remote as JobFilters['remote'],
    is_it: filters.it as JobFilters['is_it'],
    posted_within_days: filters.posted_within_days ? Number(filters.posted_within_days) : '',
    active: filters.active !== 'false',
    sort: (filters.sort === 'relevance' && !filters.q ? 'posted' : filters.sort) as JobFilters['sort'],
    page: Number(filters.page) || 1,
    page_size: PAGE_SIZE,
  }
  const jobs = useQuery({
    queryKey: ['jobs', query],
    queryFn: () => api.jobs(query),
    placeholderData: keepPreviousData,
  })
  const facetQuery: JobFilters = { ...query, job_function: '', seniority: '', skills: '' }
  const facets = useQuery({
    queryKey: ['job-facets', facetQuery],
    queryFn: () => api.jobFacets(facetQuery),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })
  const [skillInput, setSkillInput] = useState('')
  const selectedSkills = filters.skills ? filters.skills.split(',') : []
  const addSkill = (skill: string) => {
    const s = skill.trim()
    if (!s || selectedSkills.includes(s)) return
    setFilters({ skills: [...selectedSkills, s].join(',') })
    setSkillInput('')
  }
  const companyChip = useQuery({
    queryKey: ['companies', Number(filters.company_id)],
    queryFn: () => api.company(Number(filters.company_id)),
    enabled: Boolean(filters.company_id),
  })

  const data = jobs.data
  const anyFilter = Boolean(
    filters.q || filters.market || filters.country_code || filters.ats_type || filters.source_id ||
      filters.company_id || filters.company || filters.category || filters.tier || filters.remote || filters.it ||
      filters.posted_within_days || filters.found_within_days || filters.new_only || filters.active === 'false' ||
      filters.job_function || filters.seniority || filters.skills || filters.exp,
  )

  return (
    <>
      <PageHeader
        title="Jobs"
        subtitle={
          data
            ? `${data.total.toLocaleString()}${data.total_is_estimate ? '+' : ''} ${filters.new_only === 'true' ? 'new ' : ''}${filters.active === 'false' ? 'closed' : 'open'} postings`
            : 'Every opening read from the career pages.'
        }
        actions={
          <a href={api.exportUrl(query)} download className={buttonClasses('secondary')}>
            Export CSV
          </a>
        }
      />

      <div className="card overflow-hidden">
        <div className="space-y-2.5 border-b border-line px-4 py-3">
          <div className="flex flex-wrap items-center gap-2.5">
            <input
              className="field-box h-[38px] w-full sm:w-80"
              placeholder="Search title, company, location…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Segmented
              value={filters.market}
              onChange={(market) => setFilters({ market })}
              options={[
                { value: '', label: 'All' },
                { value: 'IN', label: `${MARKET_FLAG.IN} India` },
                { value: 'US', label: `${MARKET_FLAG.US} USA` },
              ]}
            />
            <Segmented
              value={filters.new_only}
              onChange={(new_only) => setFilters({ new_only, sort: new_only ? 'recent' : filters.sort })}
              options={[
                { value: '', label: 'All openings' },
                { value: 'true', label: 'New openings only' },
              ]}
            />
            <Segmented
              value={filters.it}
              onChange={(it) => setFilters({ it })}
              options={[
                { value: '', label: 'All roles' },
                { value: 'true', label: 'IT only' },
                { value: 'false', label: 'Non-IT' },
              ]}
            />
            <Select value={filters.sort} onChange={(e) => setFilters({ sort: e.target.value })} className="ml-auto w-auto">
              {filters.q && <option value="relevance">Best match</option>}
              <option value="posted">Newest posted</option>
              <option value="recent">Newest found</option>
            </Select>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <Select value={filters.job_function} onChange={(e) => setFilters({ job_function: e.target.value })} className="w-auto">
              <option value="">Any function</option>
              {facets.data?.functions.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.value} ({f.count.toLocaleString()})
                </option>
              ))}
            </Select>
            <Select value={filters.seniority} onChange={(e) => setFilters({ seniority: e.target.value })} className="w-auto">
              <option value="">Any seniority</option>
              {facets.data?.seniorities.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.value} ({f.count.toLocaleString()})
                </option>
              ))}
            </Select>
            <Select value={filters.exp} onChange={(e) => setFilters({ exp: e.target.value })} className="w-auto">
              <option value="">Any experience</option>
              {EXPERIENCE_BUCKETS.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </Select>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                addSkill(skillInput)
              }}
            >
              <input
                className="field-box h-[38px] w-48"
                list="skill-options"
                placeholder="+ Skill (Python, AWS…)"
                value={skillInput}
                onChange={(e) => {
                  const v = e.target.value
                  if (facets.data?.skills.some((s) => s.value === v)) addSkill(v)
                  else setSkillInput(v)
                }}
              />
              <datalist id="skill-options">
                {facets.data?.skills.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.count.toLocaleString()} jobs
                  </option>
                ))}
              </datalist>
            </form>
            {selectedSkills.map((s) => (
              <Badge key={s} tone="dark" className="py-1">
                {s}
                <button
                  type="button"
                  aria-label={`Remove ${s}`}
                  className="ml-1 opacity-70 hover:opacity-100"
                  onClick={() => setFilters({ skills: selectedSkills.filter((x) => x !== s).join(',') })}
                >
                  ✕
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <Select value={filters.category} onChange={(e) => setFilters({ category: e.target.value })} className="w-auto">
              <option value="">Any company type</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABEL[c]}
                </option>
              ))}
            </Select>
            <Select value={filters.tier} onChange={(e) => setFilters({ tier: e.target.value })} className="w-auto">
              <option value="">Any level</option>
              <option value="1">Level 1</option>
              <option value="2">Level 2</option>
              <option value="3">Level 3</option>
            </Select>
            <Select value={filters.country_code} onChange={(e) => setFilters({ country_code: e.target.value })} className="w-auto">
              <option value="">Any country</option>
              {COUNTRIES.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </Select>
            <input
              className="field-box h-[38px] w-44"
              placeholder="Company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
            <Select value={filters.ats_type} onChange={(e) => setFilters({ ats_type: e.target.value })} className="w-auto">
              <option value="">Any platform</option>
              {ATS_TYPES.filter((a) => a !== 'unknown').map((a) => (
                <option key={a} value={a}>
                  {ATS_LABEL[a]}
                </option>
              ))}
            </Select>
            <Select value={filters.remote} onChange={(e) => setFilters({ remote: e.target.value })} className="w-auto">
              <option value="">Remote or not</option>
              <option value="true">Remote only</option>
              <option value="false">On-site / hybrid</option>
            </Select>
            <Select
              value={filters.posted_within_days}
              onChange={(e) => setFilters({ posted_within_days: e.target.value })}
              className="w-auto"
            >
              <option value="">Posted any time</option>
              <option value="1">Last 24 hours</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </Select>
            <Select
              value={filters.found_within_days}
              onChange={(e) => setFilters({ found_within_days: e.target.value })}
              className="w-auto"
            >
              <option value="">Found any time</option>
              <option value="1">Found in last 24 hours</option>
              <option value="7">Found in last 7 days</option>
              <option value="30">Found in last 30 days</option>
            </Select>
            <Select value={filters.active} onChange={(e) => setFilters({ active: e.target.value })} className="w-auto">
              <option value="true">Open</option>
              <option value="false">Closed</option>
            </Select>
            {(filters.company_id || filters.source_id) && (
              <Badge tone="info" className="py-1">
                {companyChip.data?.name ?? (filters.company_id ? `Company #${filters.company_id}` : `Career page #${filters.source_id}`)}
                <button
                  type="button"
                  aria-label="Clear"
                  className="ml-1 opacity-70 hover:opacity-100"
                  onClick={() => setFilters({ company_id: '', source_id: '' })}
                >
                  ✕
                </button>
              </Badge>
            )}
            {anyFilter && (
              <button
                type="button"
                className="text-[13px] text-ink-muted hover:text-ink"
                onClick={() => {
                  setSearch('')
                  setCompany('')
                  setFilters({
                    q: '', market: '', it: '', country_code: '', ats_type: '', source_id: '', company_id: '', company: '',
                    category: '', tier: '', remote: '', posted_within_days: '', found_within_days: '', new_only: '',
                    job_function: '', seniority: '', skills: '', exp: '',
                    active: 'true', sort: 'posted',
                  })
                }}
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        {jobs.isError && (
          <div className="p-4">
            <ErrorBox error={jobs.error} onRetry={() => void jobs.refetch()} />
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="table-base min-w-[980px]">
            <thead>
              <tr>
                <th>Role</th>
                <th>Location</th>
                <th>Experience</th>
                <th>Pay</th>
                <th>Posted</th>
                <th>Found</th>
              </tr>
            </thead>
            <tbody className={jobs.isPlaceholderData ? 'opacity-60' : ''}>
              {jobs.isPending && <SkeletonRows cols={6} rows={10} />}
              {data?.items.map((job) => (
                <JobRow key={job.id} job={job} onOpen={() => setFilters({ job: String(job.id), page: filters.page })} />
              ))}
            </tbody>
          </table>
        </div>
        {data && data.items.length === 0 && (
          <EmptyState
            title={anyFilter ? 'No jobs match these filters' : 'No jobs stored yet'}
            body={anyFilter ? 'Try fewer filters or a shorter search.' : 'Add career pages — jobs appear here after the first crawl.'}
            action={
              !anyFilter && (
                <Link to="/sources" className={buttonClasses()}>
                  Go to career pages
                </Link>
              )
            }
          />
        )}
        {data && data.total > 0 && (
          <Pagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            estimate={data.total_is_estimate}
            onPage={(p) => setFilters({ page: String(p) })}
          />
        )}
      </div>

      {filters.job && <JobDrawer id={Number(filters.job)} onClose={() => setFilters({ job: '', page: filters.page })} />}
    </>
  )
}

function JobRow({ job, onOpen }: { job: Job; onOpen: () => void }) {
  const salary = formatSalary(job.salary_min, job.salary_max, job.salary_currency, job.salary_period)
  return (
    <tr className="cursor-pointer" onClick={onOpen}>
      <td className="max-w-[420px]">
        <p className="font-medium text-ink">{job.title}</p>
        <p className="text-[12.5px] text-ink-muted">
          {job.company_name}
          {job.job_function ? ` · ${job.job_function}` : ''}
          {job.seniority ? ` · ${job.seniority}` : ''}
        </p>
        {job.skills.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {job.skills.slice(0, 5).map((s) => (
              <span key={s} className="rounded-[5px] bg-surface-muted px-1.5 py-0.5 text-[11px] text-ink-soft">
                {s}
              </span>
            ))}
            {job.skills.length > 5 && <span className="text-[11px] text-ink-faint">+{job.skills.length - 5}</span>}
          </div>
        )}
      </td>
      <td className="max-w-[260px]">
        <p className="truncate" title={job.location ?? ''}>
          {job.location ?? '—'}
        </p>
        <p className="text-[12px] text-ink-faint">
          {job.country_code ?? ''}
          {job.is_remote ? ' · Remote' : job.workplace_type ? ` · ${humanise(job.workplace_type)}` : ''}
        </p>
      </td>
      <td className="whitespace-nowrap text-ink-soft">
        {experienceLabel(job.experience_min_years, job.experience_max_years) ?? '—'}
        {job.employment_type && <span className="block text-[12px] text-ink-faint">{job.employment_type}</span>}
      </td>
      <td className="whitespace-nowrap text-ink-soft">{salary ?? '—'}</td>
      <td className="whitespace-nowrap text-ink-soft">{job.posted_at ? relativeTime(job.posted_at) : '—'}</td>
      <td className="whitespace-nowrap">
        {job.initial_import ? (
          <span className="text-ink-muted">{relativeTime(job.first_seen_at)}</span>
        ) : (
          <Badge tone="success">New · {relativeTime(job.first_seen_at)}</Badge>
        )}
        {!job.is_active && <span className="block text-[12px] text-danger">closed</span>}
      </td>
    </tr>
  )
}
